import React, { useEffect, useMemo, useState } from 'react';
import { Scissors, Eye, CheckSquare, LayoutGrid, List } from 'lucide-react';
import { mccGet } from './mccApi';
import { toast } from 'sonner';
import { SectionHeader } from './mccUi';
import MccContentCard from './MccContentCard';
import MccProjectList from './MccProjectList';
import MccBulkActionBar from './MccBulkActionBar';
import MccQueueHelp from './MccQueueHelp';
import MccInlineFootageUploader from './MccInlineFootageUploader';

/**
 * A single generic "workload" screen used for:
 *   - Editing Queue (statuses that need editors)
 *   - Content Reviews (statuses ready for review)
 *   - Approvals (statuses awaiting an approver decision)
 * The set of statuses is chosen via the `mode` prop.
 */
export default function MccWorkQueue({ team, mode, onOpenProject, onNav }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('cards');
  const [selection, setSelection] = useState(new Set());
  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const config = ({
    editing: {
      title: 'Editing Queue',
      subtitle: 'Auto-populates from uploaded raw footage. Editors work through the list here — no need to add anything manually.',
      icon: Scissors,
      buckets: [
        { l: 'Assigned to editor', statuses: ['raw_uploaded', 'editing_in_progress', 'changes_requested'] },
        { l: 'First / revised draft uploaded', statuses: ['first_draft_uploaded', 'revised_draft_uploaded', 'final_edit_uploaded'] },
      ],
      help: {
        title: 'How the Editing Queue fills up',
        steps: [
          { n: 1, label: 'Create a project', hint: 'From an Idea, a Marketing Request, or a Filming session.' },
          { n: 2, label: 'Book & film it', hint: 'Schedule the shoot on the Filming Calendar. When done, upload the raw footage.' },
          { n: 3, label: 'Upload raw footage', hint: 'Raw Footage page → Upload. The project auto-flips to "Raw uploaded" and appears here.' },
          { n: 4, label: 'Editor picks it up', hint: 'They download from Raw Footage, edit, then upload a version — that lands in Reviews.' },
        ],
        cta: 'Go to Raw Footage',
        navTo: 'raw-footage',
      },
    },
    reviews: {
      title: 'Edited Content Reviews',
      subtitle: 'Auto-populates when editors upload a version. Preview, leave timestamped feedback, then approve or request changes.',
      icon: Eye,
      buckets: [
        { l: 'Awaiting reviewer', statuses: ['first_draft_uploaded', 'revised_draft_uploaded', 'final_edit_uploaded', 'awaiting_approval'] },
        { l: 'Changes required', statuses: ['changes_requested'] },
      ],
      help: {
        title: 'How Content Reviews fills up',
        steps: [
          { n: 1, label: 'Editor edits raw footage', hint: 'They pull it from the Raw Footage page.' },
          { n: 2, label: 'Editor uploads a version', hint: 'Inside the project drawer → Versions & Feedback → Upload edited version.' },
          { n: 3, label: 'Version lands here', hint: 'It appears in "Awaiting reviewer" for the reviewer to preview.' },
          { n: 4, label: 'Reviewer leaves feedback', hint: 'Timestamped notes on the video. Choose Approve or Request Changes.' },
        ],
        cta: 'Go to Editing Queue',
        navTo: 'editing-queue',
      },
    },
    approvals: {
      title: 'Approvals',
      subtitle: 'Auto-populates when a version is reviewed. Make the final go / no-go call before publishing.',
      icon: CheckSquare,
      buckets: [
        { l: 'Awaiting approval', statuses: ['first_draft_uploaded', 'revised_draft_uploaded', 'final_edit_uploaded', 'awaiting_approval'] },
        { l: 'Approved & ready to publish', statuses: ['final_approved', 'publishing_details_required'] },
        { l: 'Changes required', statuses: ['changes_requested'] },
      ],
      help: {
        title: 'How the Approvals queue fills up',
        steps: [
          { n: 1, label: 'Version is uploaded', hint: 'The editor uploads an edited version on the project.' },
          { n: 2, label: 'Reviewer signs off', hint: 'They mark it "Approved" or "Approved & Ready to Publish" in Content Reviews.' },
          { n: 3, label: 'It appears here', hint: 'Final approver makes the go/no-go call for publishing.' },
          { n: 4, label: 'Ready to publish', hint: 'Once approved & ready, the project moves to Publishing Schedule.' },
        ],
        cta: 'Go to Content Reviews',
        navTo: 'reviews',
      },
    },
  })[mode];

  const load = async () => {
    setLoading(true);
    try {
      const res = await mccGet('/marketing/projects');
      setProjects(res.items || []);
    } catch (e) { toast.error(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const buckets = useMemo(() => {
    if (!config) return [];
    return config.buckets.map((b) => ({
      ...b,
      items: projects.filter((p) => b.statuses.includes(p.status)),
    }));
  }, [config, projects]);

  const allItems = useMemo(
    () => buckets.flatMap((b) => b.items),
    [buckets]
  );
  const totalCount = allItems.length;

  const toggleSelect = (id) => {
    setSelection((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="p-6 space-y-6">
      <SectionHeader
        title={config.title}
        subtitle={config.subtitle}
        icon={config.icon}
        right={
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <button
              onClick={() => setView('cards')}
              data-testid="mcc-workqueue-view-cards"
              className={`px-2.5 py-1.5 ${view === 'cards' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
            ><LayoutGrid className="w-3.5 h-3.5" /></button>
            <button
              onClick={() => setView('list')}
              data-testid="mcc-workqueue-view-list"
              className={`px-2.5 py-1.5 ${view === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
            ><List className="w-3.5 h-3.5" /></button>
          </div>
        }
      />

      {/* Editors: quick-upload an edited version straight from here. */}
      {mode === 'editing' && (
        <MccInlineFootageUploader
          mode="edited"
          title="Upload an edited version"
          hint="Pick the project you edited, choose an optional platform aspect ratio, then drop the file. It appears in Content Reviews for the reviewer."
          onNav={onNav}
          onUploaded={load}
        />
      )}

      {loading ? (
        <p className="text-sm text-slate-500 p-8 text-center">Loading…</p>
      ) : totalCount === 0 ? (
        <MccQueueHelp
          title={config.help.title}
          steps={config.help.steps}
          ctaLabel={config.help.cta}
          onCta={() => onNav && onNav(config.help.navTo)}
          secondaryLabel="See a live example"
          onSecondary={() => onNav && onNav('dashboard')}
        />
      ) : view === 'list' ? (
        <MccProjectList
          items={allItems}
          nameOf={nameOf}
          onOpenProject={onOpenProject}
          selection={selection}
          onToggleSelect={toggleSelect}
          showSelect
          groupByStatus
        />
      ) : buckets.map((b) => (
        <div key={b.l}>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-sm font-bold text-slate-800">{b.l}</p>
            <span className="text-[10px] font-bold text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">{b.items.length}</span>
          </div>
          {b.items.length === 0 ? (
            <p className="text-xs text-slate-500 py-2">No projects here.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
              {b.items.map((p) => (
                <MccContentCard key={p.id} item={p} nameOf={nameOf} onClick={() => onOpenProject(p.id)} />
              ))}
            </div>
          )}
        </div>
      ))}

      <MccBulkActionBar
        selection={selection}
        team={team}
        onClear={() => setSelection(new Set())}
        onDone={() => { setSelection(new Set()); load(); }}
      />
    </div>
  );
}
